/**
 * What a project picture's menu does, wherever the picture is listed (a
 * generated one under Outputs, an upload under Context): copy it to the brand
 * library or view it there, and remove it from the project. Pictures have no
 * archive, so removing is permanent: it asks in the pegged Workshop dialog and
 * says what goes and what stays. Render `elements` once where the hook is used.
 */
import { useState, type ReactNode } from "react";
import { Eye, Library, Trash2 } from "lucide-react";
import { queue } from "@/components/Toast";
import { MediaPickerModal } from "@/app/routes/dashboard/components/media/MediaPickerModal";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuthStore } from "@/stores/authStore";
import { useWorkshopMediaMutations, type WorkshopMediaItem } from "../api/useWorkshopMedia";
import { useViewerId } from "../attribution";
import WorkshopDialog from "./WorkshopDialog";
import type { RowMenuItem } from "./WorkshopRowMenu";

export function usePictureActions({
  brandId,
  projectId,
  canWrite,
  onRemoved,
}: {
  brandId: string;
  projectId: string;
  canWrite: boolean;
  /** A picture left the project (to close its preview). */
  onRemoved?: (mediaId: string) => void;
}) {
  const viewerId = useViewerId();
  const viewer = useAuthStore((state) => state.user);
  // The brand library is the team's: a Guest keeps pictures in the project.
  const { isGuest } = usePermissions();
  const { copyToLibrary, remove } = useWorkshopMediaMutations(projectId);
  const [libraryPreviewId, setLibraryPreviewId] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<WorkshopMediaItem | null>(null);

  const copy = (item: WorkshopMediaItem) => {
    if (item.library) return;
    copyToLibrary.mutate(
      {
        item,
        by: {
          actor: "user",
          userId: viewerId,
          userName: viewer?.name?.trim() || viewer?.email || null,
          thread: null,
          deployment: null,
          at: new Date().toISOString(),
        },
      },
      {
        // Failures (someone copying it first, too) toast from the hook.
        onSuccess: (result) =>
          queue.add({
            title: "Copied to the brand library",
            description: "It's available brand-wide now. This project keeps its own copy.",
            variant: "success",
            action: {
              label: "View in library",
              onPress: () => setLibraryPreviewId(result.libraryMedia.id),
            },
          }),
      },
    );
  };

  const copying = (id: string) => copyToLibrary.isPending && copyToLibrary.variables?.item.id === id;

  /** The row menu's items for a picture, Preview first and Remove apart. */
  const menuItems = (item: WorkshopMediaItem): RowMenuItem[] => {
    const libraryId = item.library?.mediaId || null;
    return [
      { id: "open", label: "Preview", icon: Eye },
      ...(libraryId
        ? [{ id: "view", label: "View in brand library", icon: Library }]
        : !item.library && !isGuest && canWrite
          ? [{ id: "copy", label: "Copy to brand library", icon: Library, disabled: copying(item.id) }]
          : []),
      ...(canWrite ? [{ id: "remove", label: "Remove from project", icon: Trash2, danger: true }] : []),
    ];
  };

  /** Runs a menu item other than Preview. */
  const run = (id: string, item: WorkshopMediaItem) => {
    const libraryId = item.library?.mediaId || null;
    if (id === "view" && libraryId) setLibraryPreviewId(libraryId);
    else if (id === "copy") copy(item);
    else if (id === "remove") setPendingRemove(item);
  };

  const elements: ReactNode = (
    <>
      {libraryPreviewId ? (
        <MediaPickerModal
          brandId={brandId}
          isOpen
          mode="manage"
          initialTab="library"
          initialPreviewId={libraryPreviewId}
          canWrite={canWrite}
          onClose={() => setLibraryPreviewId(null)}
        />
      ) : null}
      <WorkshopDialog
        isOpen={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemove(null);
        }}
        title="Remove this picture?"
        description={
          pendingRemove
            ? pendingRemove.library
              ? `“${pendingRemove.fileName}” leaves the project. Its brand library copy stays.`
              : `“${pendingRemove.fileName}” leaves the project. It isn't in the brand library, so this is its only copy.`
            : undefined
        }
        consequence="This cannot be undone"
        cancelLabel="Keep picture"
        confirmLabel="Remove picture"
        confirmTone="danger"
        onConfirm={() => {
          if (!pendingRemove) return;
          const item = pendingRemove;
          setPendingRemove(null);
          onRemoved?.(item.id);
          remove.mutate(item.id);
        }}
      />
    </>
  );

  return { menuItems, run, copying, elements };
}
